import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BrandProfileOnboardingSkeleton, GenerationLoadingState, type GenerationStage } from "@/components/loading";
import {
      BrandProfile,
      type BrandProfileFormData,
      type BrandProfileReviewSubmission,
} from "@/components/sidebar/brand-profile";
import { useSession } from "@/hooks/use-session";
import { useSupabaseBroadcast } from "@/hooks/use-supabase-broadcast";
import { getInitialBrandProfileGenerationViewState } from "@/lib/onboarding-generation";
import { hasCompletedBrandOnboarding } from "@/lib/onboarding-progress";
import { useTRPC } from "@/lib/trpc";

export const Route = createFileRoute("/onboarding/brand/$brandId/profile")({
      component: RouteComponent,
});

const BRAND_PROFILE_GENERATION_STAGES: GenerationStage[] = [
      { id: "QUEUED", label: "Queued", progressRange: [5, 28] },
      { id: "RUNNING", label: "Generating", progressRange: [28, 90] },
];

const BRAND_PROFILE_TIPS = [
      "We’re gathering industry context, positioning, and product details to draft your initial profile.",
      "A stronger brand profile gives the persona and prompt generators better context in the next steps.",
      "You’ll still review and edit every generated field before moving forward.",
      "Later regenerations keep your manual edits protected unless you explicitly choose to overwrite them.",
];

const hasGeneratedBrandProfile = (
      brand:
            | {
                    description: string | null;
                    products: string[];
              }
            | null
            | undefined,
) =>
      Boolean(
            brand &&
                  ((brand.description?.trim().length ?? 0) > 0 ||
                        brand.products.some((product) => product.trim().length > 0)),
      );

function RouteComponent() {
      const { brandId } = Route.useParams();
      const trpc = useTRPC();
      const queryClient = useQueryClient();
      const navigate = useNavigate();
      const { sessionData, isLoading: isLoadingSession } = useSession();

      const [isSubmitting, setIsSubmitting] = useState(false);

      const invalidateBrandProfileState = useCallback(async () => {
            await Promise.all([
                  queryClient.invalidateQueries({
                        queryKey: trpc.brands.getBrand.queryKey({ id: brandId }),
                  }),
                  queryClient.invalidateQueries({
                        queryKey: trpc.brands.getBrandProfileGenerationState.queryKey({ brandId }),
                  }),
                  queryClient.invalidateQueries({
                        queryKey: trpc.brands.getOrganizationBrands.queryKey({}),
                  }),
            ]);
      }, [brandId, queryClient, trpc]);

      const { data: brandBeingCreated, isLoading: isLoadingBrand } = useQuery(
            trpc.brands.getBrand.queryOptions(
                  { id: brandId },
                  {
                        enabled: !!sessionData && !!brandId,
                        refetchOnMount: "always",
                  },
            ),
      );
      const { data: generationState } = useQuery(
            trpc.brands.getBrandProfileGenerationState.queryOptions(
                  { brandId },
                  {
                        enabled: !!sessionData && !!brandId,
                        refetchInterval: (query) =>
                              query.state.data?.activeGeneration?.mode === "INITIAL" ? 1500 : false,
                  },
            ),
      );
      const { data: brandSearchExecutionCount, isLoading: isLoadingBrandSearchExecutionCount } = useQuery(
            trpc.brands.getBrandSearchExecutionsCount.queryOptions(
                  { brandId },
                  { enabled: !!sessionData && !!brandId },
            ),
      );
      const initialBrandProfileGenerationViewState = getInitialBrandProfileGenerationViewState({
            hasBrand: Boolean(brandBeingCreated),
            hasGeneratedBrandProfile: hasGeneratedBrandProfile(brandBeingCreated),
            latestInitialGenerationStatus: generationState?.latestInitialGeneration?.status,
      });
      const isInitialBrandProfileGenerationPending = initialBrandProfileGenerationViewState === "loading";

      const updateBrandMutation = useMutation(
            trpc.brands.updateBrand.mutationOptions({
                  onSuccess: () => {
                        void invalidateBrandProfileState();
                  },
            }),
      );

      const regenerateBrandProfileMutation = useMutation(
            trpc.brands.regenerateBrandProfile.mutationOptions({
                  onSuccess: () => {
                        toast.success("Generating a fresh brand profile preview.");
                        void invalidateBrandProfileState();
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to regenerate the brand profile");
                  },
            }),
      );

      const applyPendingBrandProfileMutation = useMutation(
            trpc.brands.applyPendingBrandProfile.mutationOptions({
                  onError: (error) => {
                        toast.error(error.message || "Failed to apply the regenerated profile");
                  },
            }),
      );

      const discardPendingBrandProfileMutation = useMutation(
            trpc.brands.discardPendingBrandProfile.mutationOptions({
                  onSuccess: () => {
                        toast.success("Regenerated preview discarded.");
                        void invalidateBrandProfileState();
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to discard the regenerated preview");
                  },
            }),
      );

      useSupabaseBroadcast({
            channelName: brandId ? `brand_populated_successfully:${brandId}` : "",
            event: "brand_populated_successfully",
            callbackFn: () => {
                  void invalidateBrandProfileState();
            },
      });

      useEffect(() => {
            if (!sessionData && !isLoadingSession) {
                  navigate({ to: "/auth", replace: true });
            }
      }, [sessionData, isLoadingSession, navigate]);

      useEffect(() => {
            if (!sessionData || !brandId || !isInitialBrandProfileGenerationPending) return;

            const intervalId = window.setInterval(() => {
                  void invalidateBrandProfileState();
            }, 1500);

            return () => {
                  window.clearInterval(intervalId);
            };
      }, [brandId, invalidateBrandProfileState, isInitialBrandProfileGenerationPending, sessionData]);

      useEffect(() => {
            if (!isLoadingBrand && sessionData && !brandBeingCreated) {
                  toast.error("Brand not found or you do not have access.");
                  navigate({ to: "/onboarding/brand/new", replace: true });
            }
      }, [isLoadingBrand, sessionData, brandBeingCreated, navigate]);

      useEffect(() => {
            if (!brandBeingCreated?.slug || isLoadingBrandSearchExecutionCount) return;
            if (!hasCompletedBrandOnboarding(brandSearchExecutionCount)) return;

            navigate({
                  to: "/$brand/dashboard",
                  params: { brand: brandBeingCreated.slug },
                  replace: true,
            });
      }, [
            brandBeingCreated?.slug,
            brandSearchExecutionCount,
            isLoadingBrandSearchExecutionCount,
            navigate,
      ]);

      const handleBrandProfileSubmit = async (
            formData: BrandProfileFormData,
            reviewSubmission?: BrandProfileReviewSubmission,
      ) => {
            if (!brandBeingCreated?.id) return;

            setIsSubmitting(true);
            try {
                  if (reviewSubmission?.hasPendingReview) {
                        await applyPendingBrandProfileMutation.mutateAsync({
                              brandId,
                              overwriteFields: reviewSubmission.overwriteFields,
                              discardFields: reviewSubmission.discardFields,
                        });
                  }

                  await updateBrandMutation.mutateAsync({
                        id: brandBeingCreated.id,
                        name: formData.name,
                        website: formData.website,
                        industry: formData.industry,
                        description: formData.description,
                        products: formData.keyFeatures,
                  });

                  navigate({
                        to: "/onboarding/brand/$brandId/persona",
                        params: { brandId: brandBeingCreated.id },
                  });
            } finally {
                  setIsSubmitting(false);
            }
      };

      const handleBack = () => {
            navigate({
                  to: "/onboarding/brand/$brandId/basic",
                  params: { brandId },
            });
      };

      const handleRegenerateBrandProfile = async () => {
            await regenerateBrandProfileMutation.mutateAsync({ id: brandId });
      };

      const handleDiscardPendingBrandProfile = async () => {
            await discardPendingBrandProfileMutation.mutateAsync({ brandId });
      };

      if (!sessionData) return null;
      if (isLoadingBrand || isLoadingBrandSearchExecutionCount) {
            return <BrandProfileOnboardingSkeleton showBackButton />;
      }
      if (!brandBeingCreated) return null;
      if (isInitialBrandProfileGenerationPending) {
            const currentStageId =
                  generationState?.activeGeneration?.status === "RUNNING" ||
                  generationState?.latestInitialGeneration?.status === "RUNNING"
                        ? "RUNNING"
                        : "QUEUED";

            return (
                  <GenerationLoadingState
                        title="Generating Brand Profile"
                        estimatedDuration="Usually about a minute"
                        currentStageId={currentStageId}
                        stages={BRAND_PROFILE_GENERATION_STAGES}
                        tips={BRAND_PROFILE_TIPS}
                  />
            );
      }

      return (
            <div className="w-full">
                  <BrandProfile
                        title="Verify Brand Profile"
                        subtitle="Review your brand profile details before continuing onboarding for"
                        brandId={brandBeingCreated.id}
                        brandData={brandBeingCreated}
                        onSubmit={handleBrandProfileSubmit}
                        onBack={handleBack}
                        isSubmitting={
                              isSubmitting ||
                              updateBrandMutation.isPending ||
                              applyPendingBrandProfileMutation.isPending
                        }
                        instantlySaveable={true}
                        submitButtonText="Continue"
                        variant="onboarding"
                        generationState={generationState}
                        onDismissPendingBrandProfile={handleDiscardPendingBrandProfile}
                        isDismissingPendingBrandProfile={discardPendingBrandProfileMutation.isPending}
                        onRegenerateBrandProfile={handleRegenerateBrandProfile}
                        isRegeneratingBrandProfile={regenerateBrandProfileMutation.isPending}
                        regenerateBrandProfileDisabled={Boolean(generationState?.activeGeneration)}
                  />
            </div>
      );
}
